import { EducatorPageChrome } from "@/pages/educators/page-chrome"
import { useTranslation } from "react-i18next"
import { useEffect, useId } from "react"

import { CardContent } from "@/components/ui/card"
import { PageBody } from "@/components/ui/layout"
import { SaveButton } from "@/components/shared/action-buttons"

import Form, { DetailsSection, useClassForm } from "./form"
import { useNewProps } from "./page-props"
import { Label } from "@/components/ui/label"
import { Controller, useFormContext, SubmitHandler } from "react-hook-form"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { FormGroup } from "@/components/ui/form-field"
import { SelectInput } from "@/components/ui/select"
import { ErrorMessage } from "@hookform/error-message"
import { ErrorHint } from "@/components/ui/errorhint"
import { NewCustomClassFormValues } from "@/types/teachers/customClasses"
import { useGetSpecialitiesPrewarm } from "@/api"
import { useNavigate } from "@/lib/page-rendering"

export default function New() {
  const navigate = useNavigate()
  const formId = useId()
  const titleId = useId()
  const { custom_class } = useNewProps()
  const methods = useClassForm({
    defaultValues: { custom_class, class_scope: "personal" },
  })
  const { setValue, watch } = methods
  const {
    personal_listing_profile_id: personalListingProfileId,
    personal_listing_profile_speciality_options:
      personalListingProfileSpecialityOptions,
  } = useNewProps()
  const selectedClassScope = watch("class_scope")
  const selectedTeamProfileId = watch("team_profile_id")
  const selectedListingProfileId =
    selectedClassScope === "team"
      ? selectedTeamProfileId
        ? Number(selectedTeamProfileId)
        : null
      : personalListingProfileId
  useEffect(() => {
    setValue("custom_class.speciality_ids", [])
  }, [selectedListingProfileId, setValue])
  useGetSpecialitiesPrewarm(
    () =>
      new Map([
        [personalListingProfileId, personalListingProfileSpecialityOptions],
      ]),
    [personalListingProfileId, personalListingProfileSpecialityOptions],
  )

  const onSubmit: SubmitHandler<NewCustomClassFormValues> = (data) => {
    const { custom_class } = data
    navigate({
      href: ".",
      method: "post",
      data: {
        custom_class: {
          ...custom_class,
          listing_profile_id: selectedListingProfileId,
        },
      },
    })
  }

  return (
    <>
      <EducatorPageChrome
        titleId={titleId}
        action={<SaveButton form={formId} />}
      />
      <PageBody>
        <CardContent>
          <Form
            formId={formId}
            labelledBy={titleId}
            onSubmit={onSubmit}
            methods={methods}
          >
            <DetailsSection selectedListingProfileId={selectedListingProfileId}>
              <ClassScope />
            </DetailsSection>
          </Form>
        </CardContent>
      </PageBody>
    </>
  )
}

function ClassScope() {
  const { t } = useTranslation(["classes", "common"])
  const { control, formState, watch } =
    useFormContext<NewCustomClassFormValues>()
  const selectedClassScope = watch("class_scope")
  const { team_profile_options } = useNewProps()

  return (
    <div className="space-y-2">
      <Label id="speciality-scope-label" htmlFor="speciality-scope-input">
        {t("classes:new.class_scope.label")}
      </Label>
      <Controller
        control={control}
        name="class_scope"
        rules={{ required: t("common:errors.required") }}
        render={({ field: { onChange } }) => (
          <RadioGroup
            required
            aria-labelledby="speciality-scope-label"
            id="speciality-scope-input"
            onValueChange={(value) => onChange(value)}
            className="items-top flex w-full flex-col space-y-2 space-x-2"
            defaultValue="personal"
          >
            <div className="border-muted has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/10 has-data-[state=checked]:hover:bg-primary/15 has-data-[state=unchecked]:hover:bg-brand-dark-grey/10 flex flex-col gap-2 rounded-md border px-4 py-2 transition-colors">
              <div className="flex flex-row items-center">
                <RadioGroupItem
                  value="personal"
                  id="personal"
                  className="peer"
                  aria-labelledby="personal-label"
                  aria-describedby="personal-description"
                />
                <Label
                  htmlFor="personal"
                  className="flex items-center gap-2 rounded-md px-4 py-2"
                >
                  <div className="flex flex-col">
                    <span className="font-medium" id="personal-label">
                      {t("classes:new.class_scope.personal_label")}
                    </span>
                    <span className="text-xs" id="personal-description">
                      {t("classes:new.class_scope.personal_description")}
                    </span>
                  </div>
                </Label>
              </div>
            </div>

            <div className="border-muted has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/10 has-data-[state=checked]:hover:bg-primary/15 has-data-[state=unchecked]:hover:bg-brand-dark-grey/10 flex flex-col gap-2 rounded-md border px-4 py-2 transition-colors">
              <div className="flex flex-row items-center">
                <RadioGroupItem
                  value="team"
                  id="team"
                  className="peer"
                  aria-labelledby="team-label"
                  aria-describedby="team-description"
                />
                <Label
                  htmlFor="team"
                  className="flex items-center gap-2 rounded-md px-4 py-2"
                >
                  <div className="flex flex-col">
                    <span className="font-medium" id="team-label">
                      {t("classes:new.class_scope.team_label")}
                    </span>
                    <span className="text-xs" id="team-description">
                      {t("classes:new.class_scope.team_description")}
                    </span>
                  </div>
                </Label>
              </div>
              <FormGroup
                htmlFor="team-select"
                label={t("classes:new.team_select.label")}
              >
                <Controller
                  name="team_profile_id"
                  control={control}
                  rules={{
                    required:
                      selectedClassScope === "team"
                        ? t("common:errors.required")
                        : undefined,
                  }}
                  render={({ field }) => (
                    <SelectInput
                      className="w-64"
                      id="team-select"
                      value={field.value?.toString() || ""}
                      onChange={(listing_profile_id) => {
                        field.onChange(parseInt(listing_profile_id, 10))
                      }}
                      options={team_profile_options.map(({ label, value }) => ({
                        label,
                        value: value.toString(),
                      }))}
                      placeholder={t("classes:new.team_select.placeholder")}
                    />
                  )}
                />
                <ErrorMessage
                  name="team_profile_id"
                  errors={formState.errors}
                  render={ErrorHint}
                />
              </FormGroup>
            </div>
          </RadioGroup>
        )}
      />
      <ErrorMessage
        name="class-scope"
        errors={formState.errors}
        render={ErrorHint}
      />
    </div>
  )
}
